import { S, hasGame } from '../core/game';
import type { Decor } from '../world/mapdef';
import { OUTLINE, block, ellipse, hash, rect, shadow, shade, sun, type Ctx } from './paint';
import { registerPropDrawers } from './propRegistry';
import { drawCharacter, drawCat, type CharLook, type Facing } from './sprites';
import { wind } from './nature';

/**
 * Town props (`tw_*`): the small things that make the hub feel like a Taiwanese neighbourhood —
 * utility poles with cables, rooftop water tanks and tin sheds, arcades (騎樓), a corner-store sign,
 * a breakfast stall, the land-god temple, a park pavilion, stray dogs and cats, bleachers...
 * Everything static and big goes through `art` (bitmap cache) because outdoor props redraw every frame.
 */

const CJK = '"PingFang TC", "Noto Sans TC", "Noto Sans CJK TC", "Microsoft JhengHei", sans-serif';

function txt(c: Ctx, s: string, x: number, y: number, size: number, col: string, weight = 900, shadowCol?: string): void {
  c.font = `${weight} ${size}px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  if (shadowCol) {
    c.fillStyle = shadowCol;
    c.fillText(s, x + 0.4, y + 0.5);
  }
  c.fillStyle = col;
  c.fillText(s, x, y);
}

function vgrad(c: Ctx, x: number, y: number, w: number, h: number, top: string, bottom: string): void {
  const g = c.createLinearGradient(0, y, 0, y + h);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  c.fillStyle = g;
  c.fillRect(x, y, w, h);
}

function poly(c: Ctx, col: string, pts: number[]): void {
  c.fillStyle = col;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.closePath();
  c.fill();
}

function line(c: Ctx, col: string, w: number, pts: number[]): void {
  c.strokeStyle = col;
  c.lineWidth = w;
  c.beginPath();
  c.moveTo(pts[0], pts[1]);
  for (let i = 2; i < pts.length; i += 2) c.lineTo(pts[i], pts[i + 1]);
  c.stroke();
}

const isNight = () => sun.alpha < 0.8;
const H = (h: number, m = 0) => h * 60 + m;
const timeNow = () => (hasGame() ? S().time : H(12));

/** Offscreen cache for static drawings (built once per zoom level). */
const bitmaps = new Map<string, { cv: HTMLCanvasElement; s: number }>();
function art(c: Ctx, key: string, x0: number, y0: number, w: number, h: number, draw: (cc: Ctx) => void): void {
  const m = c.getTransform();
  const s = Math.max(1, Math.round(Math.hypot(m.a, m.b) * 2) / 2);
  let e = bitmaps.get(key);
  if (!e || e.s !== s) {
    const cv = document.createElement('canvas');
    cv.width = Math.ceil(w * s);
    cv.height = Math.ceil(h * s);
    const cc = cv.getContext('2d')!;
    cc.scale(s, s);
    cc.translate(-x0, -y0);
    draw(cc);
    e = { cv, s };
    bitmaps.set(key, e);
  }
  c.drawImage(e.cv, x0, y0, w, h);
}

// ---------------------------------------------------------------------------
// People used inside props (vendors, seated folks)
// ---------------------------------------------------------------------------

const VENDOR: CharLook = { skin: '#e8b48c', hair: '#2a1d16', hairStyle: 'bun', top: '#f4f1ea', topShade: '#cfc8bb', bottom: '#3a3a3e', shoes: '#1d1814', apron: '#e2493b' };
const VENDOR_PM: CharLook = { skin: '#d9a47a', hair: '#1d1a22', hairStyle: 'cap', hat: '#1b1d24', top: '#1b1d24', topShade: '#0e1014', bottom: '#2f3442', shoes: '#1d1814', apron: '#3a3a3e' };
const JUICE: CharLook = { skin: '#c88a5a', hair: '#9a948a', hairStyle: 'cap', hat: '#ffd34e', top: '#3a8fd9', topShade: '#2a6fb0', bottom: '#5b4636', shoes: '#f4f1ea', apron: '#f4f1ea' };
const ELDER: CharLook = { skin: '#d9a47a', hair: '#c9c4bc', hairStyle: 'short', top: '#7a8aa0', topShade: '#5a6a80', bottom: '#3a3a3e', shoes: '#1d1814' };
const SKINS = ['#f6cfa8', '#f3c9a0', '#e8b48c', '#d9a47a', '#c88a5a', '#f8d8bc'];
const TOPS: [string, string][] = [['#ff6aa0', '#d04a80'], ['#3a8fd9', '#2a6fb0'], ['#f4f1ea', '#cfc8bb'], ['#ffd34e', '#d8b030'], ['#57c278', '#3a9a58'], ['#9b5fc0', '#7a3fa0'], ['#e2493b', '#b0302a'], ['#ff9f43', '#d07a2a']];
const STYLES = ['short', 'long', 'pony', 'bun', 'cap', 'pigtails', 'messy', 'neat', 'curly'] as const;
/** Sixteen looks for seated people; every fifth one is an elder, 3 and 10 are kids. */
const LOOKS: CharLook[] = Array.from({ length: 16 }, (_, i) => {
  const elder = i % 5 === 4;
  const [top, topShade] = TOPS[(i * 3) % TOPS.length];
  return {
    skin: SKINS[i % SKINS.length],
    hair: elder ? ['#9a948a', '#c9c4bc'][i % 2] : ['#1d1a22', '#2a1d16', '#6b3f22', '#3a2418'][i % 4],
    hairStyle: STYLES[(i * 7) % STYLES.length],
    hat: ['#e2493b', '#1b1d24', '#3a8fd9', '#ffffff'][i % 4],
    top,
    topShade,
    bottom: ['#2f3442', '#3a4a7a', '#1d1a22', '#7a5a3a', '#4a6a8a'][i % 5],
    shoes: ['#f4f1ea', '#1d1814', '#e2493b'][i % 3],
    dress: i % 3 === 1,
    glasses: i % 6 === 2,
    small: i % 7 === 3,
  };
});
const idle = (t: number, k = 0) => ({ facing: 'down' as Facing, phase: 0, moving: false, blink: ((t + k * 1.3) % 4) < 0.12, breath: (t * 0.45 + k * 0.37) % 1 });

// ---------------------------------------------------------------------------
// Street furniture
// ---------------------------------------------------------------------------

/** Concrete utility pole. `w/2` = span of the three cables running east; variant bits: 1 transformer, 2 cable north across the street (`h` px), 4 red notice plate. */
function pole(c: Ctx, d: Decor, _t: number): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  const span = (d.w ?? 0) / 2;
  shadow(c, x, y, 3, 1.2, 0.3, 50);
  rect(c, '#55595e', x - 1.8, y - 70, 3.6, 70);
  rect(c, '#8a9096', x - 0.9, y - 69, 1.2, 68);
  rect(c, '#3a3e42', x - 1.8, y - 70, 3.6, 1);
  rect(c, '#4a4e52', x - 8, y - 64, 16, 1.4);
  rect(c, '#4a4e52', x - 8, y - 58, 16, 1.4);
  for (const dx of [-7, 0, 7]) rect(c, '#e0e0d8', x + dx - 0.7, y - 66.4, 1.4, 2.4);
  for (const dx of [-7, 7]) rect(c, '#e0e0d8', x + dx - 0.7, y - 60.4, 1.4, 2.4);
  if (v & 1) {
    block(c, x + 3, y - 54, 7, 10, '#6a6e72', '#8a9096', '#4a4e52');
    rect(c, '#3a3e42', x + 3.5, y - 51, 6, 0.6);
    rect(c, '#3a3e42', x + 3.5, y - 48, 6, 0.6);
    line(c, 'rgba(30,24,20,0.7)', 0.5, [x + 6, y - 54, x + 4, y - 58]);
  }
  // Address / ad plate wrapped around the pole, the way Taiwanese poles carry little signs.
  block(c, x - 3.4, y - 32, 6.8, 9, v & 4 ? '#e2493b' : '#2f6fb0');
  rect(c, '#ffffff', x - 2.2, y - 30.4, 4.4, 1);
  rect(c, '#ffffff', x - 2.2, y - 28, 4.4, 1);
  rect(c, '#ffffff', x - 2.2, y - 25.6, 4.4, 1);
  c.strokeStyle = 'rgba(30,24,20,0.72)';
  c.lineWidth = 0.6;
  if (span > 0) {
    for (const [dy, sag] of [[-66, 8], [-64, 9.5], [-58, 10.5]]) {
      c.beginPath();
      c.moveTo(x, y + dy);
      c.quadraticCurveTo(x + span / 2, y + dy + sag * 2, x + span, y + dy);
      c.stroke();
    }
  }
  if (v & 2) {
    const drop = d.h ?? 80;
    for (const dx of [-1, 1.5]) {
      c.beginPath();
      c.moveTo(x + dx, y - 62);
      c.quadraticCurveTo(x + dx + 6, y - 62 - drop * 0.45, x + dx, y - 62 - drop);
      c.stroke();
    }
  }
}

function flagpole(c: Ctx, d: Decor, _t: number): void {
  const { x, y } = d;
  shadow(c, x, y, 7, 2.2, 0.3, 60);
  block(c, x - 8, y - 5, 16, 5, '#c8c4bc', '#e8e4dc', '#9a968e');
  block(c, x - 5, y - 8, 10, 3, '#b0aca4', '#d0ccc4', '#8a867e');
  rect(c, '#3a3e44', x - 1.2, y - 86, 2.4, 78);
  rect(c, '#d0d4d8', x - 0.5, y - 86, 0.8, 78);
  ellipse(c, '#ffd34e', x, y - 87, 1.8, 1.8);
  // The flag, rippling in the wind.
  const w = wind.at(x, y);
  const ft = wind.time();
  const fw = 24, fh = 15, x0 = x + 1.2, y0 = y - 85;
  const ripple = (k: number) => Math.sin(ft * 7 + k * 6) * (0.6 + w) * 1.6 * k;
  c.save();
  c.beginPath();
  c.moveTo(x0, y0);
  for (let k = 0; k <= 1.001; k += 0.1) c.lineTo(x0 + k * fw, y0 + ripple(k));
  for (let k = 1; k >= 0; k -= 0.1) c.lineTo(x0 + k * fw, y0 + fh + ripple(k));
  c.closePath();
  c.clip();
  rect(c, '#d4242a', x0, y0 - 4, fw, fh + 8);
  rect(c, '#1f3a8a', x0, y0 - 4, fw / 2, fh / 2 + 4);
  const sx = x0 + fw / 4, sy = y0 + fh / 4;
  ellipse(c, '#ffffff', sx, sy, 2.2, 2.2);
  c.strokeStyle = '#ffffff';
  c.lineWidth = 0.7;
  for (let i = 0; i < 12; i++) {
    const a = (i * Math.PI) / 6;
    c.beginPath();
    c.moveTo(sx + Math.cos(a) * 2.7, sy + Math.sin(a) * 2.7);
    c.lineTo(sx + Math.cos(a) * 3.8, sy + Math.sin(a) * 3.8);
    c.stroke();
  }
  for (let k = 0.2; k < 1; k += 0.27) rect(c, 'rgba(0,0,0,0.13)', x0 + k * fw, y0 - 4, 2.2, fh + 8);
  c.restore();
}

function noticeBoard(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = 40, v = d.variant ?? 0;
  art(c, `nb:${x}:${y}:${v}`, x - w / 2 - 6, y - 44, w + 12, 48, (cc) => {
    shadow(cc, x, y, w / 2, 2.4);
    for (const lx of [x - w / 2 + 3, x + w / 2 - 5.4]) rect(cc, '#4a4e52', lx, y - 12, 2.4, 12);
    block(cc, x - w / 2, y - 36, w, 25, '#4f8a3a', '#6faa4a', '#2f6a2a');
    rect(cc, '#eef1f3', x - w / 2 + 2, y - 34, w - 4, 21);
    const cols = ['#fff6dc', '#ffe08a', '#bfe6ff', '#ffd0e0', '#fff6dc', '#d8f0c0'];
    for (let i = 0; i < 6; i++) {
      const px = x - w / 2 + 4 + (i % 3) * 12, py = y - 32 + Math.floor(i / 3) * 10;
      rect(cc, cols[(i + v) % 6], px, py, 10, 8);
      for (let k = 0; k < 3; k++) rect(cc, 'rgba(60,50,40,0.5)', px + 1.5, py + 2 + k * 2, 7 - (k % 2) * 2, 0.6);
      if (i % 2 === v % 2) rect(cc, '#e2493b', px + 1, py + 1, 1.6, 1.6);
    }
    rect(cc, 'rgba(255,255,255,0.35)', x - w / 2 + 2, y - 34, w - 4, 0.8);
    block(cc, x - w / 2 - 3, y - 41, w + 6, 6, '#2f6fb0', '#4f8fd0', '#1f4f80');
    txt(cc, '公佈欄', x, y - 38.2, 4.2, '#ffffff', 900);
  });
}

function bike(c: Ctx, x: number, y: number, col: string): void {
  c.strokeStyle = '#2a2a30';
  c.lineWidth = 0.9;
  for (const dx of [-5, 5]) {
    c.beginPath();
    c.arc(x + dx, y - 4, 3.4, 0, Math.PI * 2);
    c.stroke();
  }
  line(c, col, 1.1, [x - 5, y - 4, x - 1, y - 9, x + 3, y - 9, x + 5, y - 4]);
  line(c, col, 1.1, [x - 1, y - 9, x + 1, y - 4, x - 5, y - 4]);
  rect(c, '#2a2a30', x - 2.4, y - 11, 3, 1.2);
  line(c, '#55606a', 0.8, [x + 3, y - 9, x + 3.6, y - 12, x + 5.4, y - 12]);
}

/** Student bicycles parked in a row under a corrugated shelter. */
function bikeShed(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 64;
  art(c, `bs:${x}:${y}:${w}`, x - w / 2 - 6, y - 38, w + 12, 42, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 3, 0.25);
    rect(cc, 'rgba(0,0,0,0.1)', x - w / 2 - 4, y - 14, w + 8, 14);
    const n = Math.floor(w / 13);
    for (let i = 0; i < n; i++) {
      const bx = x - w / 2 + 7 + i * 13;
      const col = ['#3a8fd9', '#e2493b', '#57c278', '#f4f1ea', '#ffd34e', '#9b5fc0'][Math.floor(hash(i, 1, 7) * 6)];
      bike(cc, bx, y - 2 + (i % 2), col);
    }
    for (const px of [x - w / 2, x + w / 2 - 2]) rect(cc, '#4a4e52', px, y - 30, 2, 30);
    cc.save();
    cc.globalAlpha = 0.86;
    block(cc, x - w / 2 - 4, y - 35, w + 8, 8, '#7aa0a8', '#9ac0c8', '#5a8088');
    cc.restore();
    for (let i = x - w / 2 - 2; i < x + w / 2 + 4; i += 3) rect(cc, 'rgba(255,255,255,0.18)', i, y - 34, 1, 6);
    rect(cc, 'rgba(0,0,0,0.18)', x - w / 2 - 4, y - 28, w + 8, 1.5);
    block(cc, x - 12, y - 38, 24, 5, '#2f6fb0', '#4f8fd0', '#1f4f80');
    txt(cc, '腳踏車停車場', x, y - 35.6, 3.2, '#ffffff', 900);
  });
}

/** Taiwan's three-bin set: recycling, food waste, general. */
function recycle(c: Ctx, d: Decor): void {
  const { x, y } = d;
  shadow(c, x, y, 14, 2.4);
  const bins: [string, string][] = [['#2f6fb0', '回收'], ['#3a9a4a', '廚餘'], ['#7a7e84', '垃圾']];
  bins.forEach(([col, lab], i) => {
    const bx = x - 9 + i * 9;
    block(c, bx - 4, y - 11, 8, 11, col, shade(col, 0.25), shade(col, -0.3));
    block(c, bx - 4.6, y - 13.4, 9.2, 2.8, shade(col, -0.15));
    rect(c, 'rgba(0,0,0,0.3)', bx - 1.6, y - 12.6, 3.2, 1.2);
    txt(c, lab, bx, y - 6, 2.8, '#ffffff', 800);
  });
}

/** Horizontal lit sign band of the corner store (fictional LAI LAI MART). */
function storeSign(c: Ctx, d: Decor, _t: number): void {
  const { x, y } = d;
  const w = d.w ?? 160;
  const night = isNight();
  art(c, `ss:${x}:${y}:${w}:${night ? 1 : 0}`, x - w / 2 - 2, y - 17, w + 4, 20, (cc) => {
    rect(cc, 'rgba(0,0,0,0.25)', x - w / 2 + 1, y - 1, w, 2);
    block(cc, x - w / 2, y - 15, w, 15, night ? '#fffaf0' : '#f4f1ea', '#ffffff', '#c8c0b0');
    rect(cc, '#f08a24', x - w / 2 + 1, y - 14, w - 2, 2.6);
    rect(cc, '#2f9a5a', x - w / 2 + 1, y - 11.4, w - 2, 2.6);
    rect(cc, '#e2493b', x - w / 2 + 1, y - 8.8, w - 2, 1.6);
    txt(cc, '來來超商', x - w / 4 + 4, y - 4, 6.2, '#1b6a3a', 900);
    txt(cc, 'LAI LAI MART · 24h', x + w / 4 - 4, y - 4, 3.8, '#2a1810', 900);
    for (let i = 0; i < Math.floor(w / 24); i++) rect(cc, 'rgba(0,0,0,0.08)', x - w / 2 + 12 + i * 24, y - 7, 0.6, 6);
  });
  if (night) ellipse(c, 'rgba(255,245,215,0.3)', x, y - 7, w / 2 + 6, 13);
}

/** 早餐店 in the morning, 鹽酥雞 in the evening, shutters down otherwise. */
function breakfast(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const tm = timeNow();
  const mode = tm >= H(5, 30) && tm < H(13, 30) ? 'am' : tm >= H(16, 30) && tm < H(23, 30) ? 'pm' : 'off';
  shadow(c, x, y, 22, 3.2);
  if (mode !== 'off') drawCharacter(c, mode === 'pm' ? VENDOR_PM : VENDOR, x - 3, y - 7, idle(t, 2));
  block(c, x - 20, y - 14, 40, 14, '#d0c8b8', '#e8e0d0', '#8a8278');
  rect(c, '#6a6058', x - 19, y - 3, 38, 0.8);
  rect(c, '#b8b0a0', x - 19, y - 13, 38, 0.8);
  for (const px of [x - 20, x + 18]) rect(c, '#4a4e52', px, y - 34, 2, 20);
  const aw = mode === 'pm' ? '#f2c230' : '#e2493b';
  block(c, x - 23, y - 39, 46, 8, aw, shade(aw, 0.3), shade(aw, -0.3));
  for (let i = 0; i < 7; i++) if (i % 2) rect(c, '#ffffff', x - 22 + i * 6.3, y - 38.4, 6.3, 6.8);
  rect(c, 'rgba(0,0,0,0.18)', x - 23, y - 31.6, 46, 1);
  txt(c, mode === 'pm' ? '鹽酥雞' : '早安美芝城', x, y - 35.4, 5, mode === 'pm' ? '#2a1810' : '#ffffff', 900, 'rgba(0,0,0,0.35)');
  if (mode === 'off') {
    block(c, x - 19, y - 32, 38, 18, '#9aa0a6', '#b8bec4', '#6a7076');
    for (let k = y - 30; k < y - 15; k += 2.4) rect(c, 'rgba(0,0,0,0.15)', x - 18, k, 36, 0.8);
    block(c, x - 8, y - 25, 16, 6, '#ffffff');
    txt(c, '休息中', x, y - 22, 3.6, '#c0392b', 900);
    return;
  }
  block(c, x + 7, y - 31, 14, 13, '#2a2a30', '#3a3a40', '#1a1a20');
  txt(c, mode === 'pm' ? '雞排 70' : '蛋餅 35', x + 14, y - 27.4, 3, '#ffd34e', 900);
  txt(c, mode === 'pm' ? '甜不辣 40' : '奶茶 25', x + 14, y - 22.4, 3, '#ffffff', 800);
  if (mode === 'am') {
    for (let i = 0; i < 3; i++) block(c, x - 17, y - 18 - i * 4, 12, 4, '#c8a870', '#e0c890', '#9a7a4a');
    ellipse(c, '#8a8e94', x - 11, y - 30.4, 6, 1.6);
    block(c, x + 1, y - 18, 14, 5, '#3a3e44', '#5a5e64', '#2a2e34');
    ellipse(c, '#ffd27a', x + 5, y - 16.2, 3, 1.6);
    ellipse(c, '#ffe8a0', x + 10.5, y - 15.6, 2.6, 1.4);
    for (let i = 0; i < 4; i++) {
      const k = (t * 0.6 + i / 4) % 1;
      ellipse(c, `rgba(255,255,255,${0.45 * (1 - k)})`, x - 11 + Math.sin(t * 2 + i) * 2, y - 32 - k * 14, 1.5 + k * 3, 1 + k * 2);
    }
  } else {
    block(c, x - 17, y - 20, 14, 6, '#9aa0a6', '#c0c6cc', '#6a7076');
    rect(c, '#ffb43a', x - 16, y - 19, 12, 2);
    rect(c, '#2a1810', x - 15, y - 17, 10, 1);
    for (let i = 0; i < 3; i++) {
      const k = (t * 0.5 + i / 3) % 1;
      ellipse(c, `rgba(255,255,255,${0.35 * (1 - k)})`, x - 10 + Math.sin(t * 2 + i) * 2, y - 24 - k * 12, 1.4 + k * 2.5, 1 + k * 1.6);
    }
    const sw = Math.sin(t * 1.8) * 1;
    line(c, '#2a1810', 0.5, [x + 21, y - 39, x + 21 + sw, y - 34]);
    ellipse(c, OUTLINE, x + 21 + sw, y - 30, 3.8, 4.6);
    ellipse(c, '#e2493b', x + 21 + sw, y - 30, 3.2, 4);
    rect(c, '#ffd34e', x + 21 + sw - 2.6, y - 30.4, 5.2, 0.7);
    if (isNight()) ellipse(c, 'rgba(255,120,60,0.3)', x + 21 + sw, y - 30, 8, 8);
  }
}

/** Taiwan Post's green (ordinary) and red (express/airmail) boxes side by side. */
function postbox(c: Ctx, d: Decor): void {
  const { x, y } = d;
  shadow(c, x, y, 10, 2);
  ([['#2f8a4a', -5], ['#d0453a', 5]] as [string, number][]).forEach(([col, dx]) => {
    const bx = x + dx;
    block(c, bx - 4, y - 14, 8, 14, col, shade(col, 0.3), shade(col, -0.3));
    block(c, bx - 4.6, y - 16.4, 9.2, 3, shade(col, -0.1));
    rect(c, '#1b1d24', bx - 2.6, y - 11.4, 5.2, 1.2);
    rect(c, '#ffffff', bx - 2.6, y - 8.6, 5.2, 2.6);
    txt(c, '郵', bx, y - 7.3, 2.4, col, 900);
  });
}

/** A tugou-style stray dog: sleeping (variant even) or sitting with its tail wagging. Bits 2-3 pick the coat, 8 flips it. */
function dog(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  const fur = ['#6a4a30', '#1f1d22', '#c8a060', '#e8dcc8'][(v >> 1) & 3];
  const belly = shade(fur, 0.3);
  const dir = v & 8 ? -1 : 1;
  shadow(c, x, y, 8, 2);
  if ((v & 1) === 0) {
    const br = Math.sin(t * 2) * 0.4;
    ellipse(c, OUTLINE, x, y - 3.5, 8.8, 4.2 + br);
    ellipse(c, fur, x, y - 3.5, 7.8, 3.2 + br);
    ellipse(c, belly, x - dir * 2, y - 2.6, 3.5, 1.4);
    ellipse(c, OUTLINE, x + dir * 6.5, y - 4.4, 4.4, 3.4);
    ellipse(c, fur, x + dir * 6.5, y - 4.4, 3.6, 2.6);
    rect(c, OUTLINE, x + dir * 6 - 1.5, y - 8.4, 2.6, 2.8);
    rect(c, fur, x + dir * 6 - 1, y - 8, 1.6, 2.2);
    rect(c, '#1b1d24', x + dir * 9.4 - 0.6, y - 4.8, 1.4, 1);
    rect(c, 'rgba(0,0,0,0.45)', x + dir * 5.4 - 1, y - 5.2, 2, 0.6);
    rect(c, OUTLINE, x - dir * 8 - 1, y - 3, 2.4, 1.6);
    rect(c, fur, x - dir * 8 - 0.6, y - 2.7, 1.6, 1);
    const k = (t * 0.4) % 1;
    txt(c, 'z', x + dir * 8 + k * 4, y - 10 - k * 8, 3 + k * 2, `rgba(255,255,255,${0.85 * (1 - k)})`, 900);
  } else {
    const wag = Math.sin(t * 8) * 2.5;
    rect(c, OUTLINE, x - dir * 6 - 1.5 + wag * 0.3 * dir, y - 10 + wag * 0.4, 3.5, 2);
    rect(c, fur, x - dir * 6 - 1 + wag * 0.3 * dir, y - 9.6 + wag * 0.4, 2.5, 1.2);
    block(c, x - 5, y - 9, 10, 9, fur, undefined, undefined);
    ellipse(c, belly, x + dir * 1, y - 4.5, 2.6, 2.4);
    ellipse(c, OUTLINE, x + dir * 2.5, y - 13, 5, 4.4);
    ellipse(c, fur, x + dir * 2.5, y - 13, 4.2, 3.6);
    for (const ex of [-3, 1]) {
      rect(c, OUTLINE, x + dir * 2.5 + ex, y - 17.6, 2.2, 3.4);
      rect(c, fur, x + dir * 2.5 + ex + 0.4, y - 17.2, 1.4, 2.6);
    }
    rect(c, '#1b1d24', x + dir * 5.6 - 0.5, y - 13.2, 1.4, 1.1);
    rect(c, '#1b1d24', x + dir * 1.4, y - 14.4, 0.9, 0.9);
    rect(c, '#ff9fb0', x + dir * 5 - 0.6, y - 11.4, 1.2, 1.4);
    rect(c, fur, x - 3.6, y - 1.5, 2.6, 1.5);
    rect(c, fur, x + 1, y - 1.5, 2.6, 1.5);
    rect(c, '#e2493b', x - 4, y - 9.4, 8, 1);
  }
}

/** A cat perched on something: `h` lifts it (wall top, machine roof, scooter seat). */
function cat(c: Ctx, d: Decor, t: number): void {
  const v = d.variant ?? 0;
  const fur = ['#f0a35e', '#2a2a30', '#d8d2c8', '#8a8078'][v & 3];
  drawCat(c, d.x, d.y - (d.h ?? 0), { facing: v & 4 ? 'left' : 'right', phase: 0, moving: false, blink: false, breath: 0 }, t + v, false, fur);
}

/** Little land-god temple (土地公廟): swallowtail roof, red pillars, gold plaque, lanterns and incense. */
function temple(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const night = isNight();
  art(c, `tp:${x}:${y}`, x - 42, y - 64, 84, 68, (cc) => {
    shadow(cc, x, y, 30, 4, 0.3, 30);
    block(cc, x - 30, y - 4, 60, 4, '#b0a898', '#c8c0b0', '#8a8278');
    for (let i = x - 28; i < x + 28; i += 6) rect(cc, 'rgba(0,0,0,0.08)', i, y - 3.5, 0.6, 3);
    block(cc, x - 23, y - 36, 46, 32, '#c0392b', '#e05a48', '#8a2a1e');
    rect(cc, '#3a1a10', x - 15, y - 31, 30, 23);
    vgrad(cc, x - 15, y - 31, 30, 23, 'rgba(255,170,60,0)', 'rgba(255,170,60,0.4)');
    block(cc, x - 10, y - 15, 20, 5, '#8a2a1e', '#a8392a', '#5a1a10');
    rect(cc, '#ffd34e', x - 9, y - 14.6, 18, 0.8);
    ellipse(cc, '#ffd34e', x, y - 24, 4.6, 5.4);
    rect(cc, '#e2493b', x - 3, y - 22, 6, 7);
    ellipse(cc, '#f6cfa8', x, y - 25.5, 2.4, 2.4);
    rect(cc, '#ffffff', x - 1.6, y - 23.2, 3.2, 2.4);
    rect(cc, '#1b1d24', x - 2.2, y - 28, 4.4, 1.2);
    for (const dx of [-7, 7]) {
      rect(cc, '#ffd34e', x + dx - 1, y - 20, 2, 5);
      ellipse(cc, '#ff9a3a', x + dx, y - 21, 1, 1.5);
    }
    for (const dx of [-11, 10]) block(cc, x + dx - 1.5, y - 18, 3, 3, '#e2493b');
    for (const px of [x - 23, x + 19]) {
      block(cc, px, y - 38, 4, 34, '#c0392b', '#e05a48', '#8a2a1e');
      rect(cc, '#ffd34e', px + 1, y - 33, 2, 12);
    }
    block(cc, x - 13, y - 44, 26, 8, '#2a1810', '#4a3020', '#1a0a05');
    rect(cc, '#ffd34e', x - 12, y - 43, 24, 6);
    txt(cc, '福德宮', x, y - 39.6, 5.2, '#2a1810', 900);
    poly(cc, '#2a7a4a', [x - 36, y - 46, x + 36, y - 46, x + 27, y - 56, x - 27, y - 56]);
    for (let i = x - 35; i < x + 35; i += 3) rect(cc, 'rgba(255,255,255,0.13)', i, y - 55, 1, 9);
    for (let k = y - 54; k < y - 46; k += 2.6) rect(cc, 'rgba(0,0,0,0.12)', x - 35, k, 70, 0.6);
    rect(cc, '#e8702a', x - 29, y - 57.4, 58, 2.6);
    poly(cc, '#e8702a', [x - 29, y - 55, x - 37, y - 63, x - 33.5, y - 63, x - 27, y - 57.4]);
    poly(cc, '#e8702a', [x + 29, y - 55, x + 37, y - 63, x + 33.5, y - 63, x + 27, y - 57.4]);
    for (const dx of [-17, 17]) {
      ellipse(cc, '#3fa06a', x + dx, y - 59.2, 3.2, 1.8);
      rect(cc, '#ffd34e', x + dx + (dx < 0 ? 2 : -3), y - 61.4, 1.2, 2.4);
    }
    ellipse(cc, '#ff7a3a', x, y - 60, 2.8, 2.8);
    ellipse(cc, '#ffd34e', x, y - 60, 1.8, 1.8);
    rect(cc, '#8a2a1e', x - 36, y - 46.4, 72, 1.8);
    block(cc, x - 7, y - 11, 14, 7, '#b8860b', '#e0b030', '#8a6008');
    rect(cc, '#8a6008', x - 6, y - 10, 12, 0.8);
    for (const dx of [-6, 6]) rect(cc, '#8a6008', x + dx - 1, y - 5, 2, 3);
    for (const dx of [-7, 7]) block(cc, x + dx - 1.4, y - 14, 2.8, 3, '#e0b030');
  });
  for (const dx of [-2, 0, 2]) line(c, '#c0392b', 0.5, [x + dx, y - 10, x + dx * 1.3, y - 16]);
  for (let i = 0; i < 3; i++) {
    const k = (t * 0.35 + i / 3) % 1;
    ellipse(c, `rgba(230,230,230,${0.4 * (1 - k)})`, x + Math.sin(t * 1.5 + i * 2) * 2.5, y - 17 - k * 22, 1 + k * 3, 0.8 + k * 2);
  }
  const sw = Math.sin(t * 1.6) * 1.2;
  for (const dx of [-28, 28]) {
    line(c, '#2a1810', 0.5, [x + dx, y - 46, x + dx + sw, y - 42]);
    ellipse(c, OUTLINE, x + dx + sw, y - 37, 4.4, 5.4);
    ellipse(c, '#e2493b', x + dx + sw, y - 37, 3.7, 4.7);
    rect(c, '#ffd34e', x + dx + sw - 3, y - 37.4, 6, 0.8);
    txt(c, '福', x + dx + sw, y - 37, 3.6, '#ffd34e', 900);
    rect(c, '#ffd34e', x + dx + sw - 0.6, y - 32, 1.2, 3);
    if (night) ellipse(c, 'rgba(255,90,60,0.3)', x + dx + sw, y - 37, 9, 10);
  }
}

/** Park pavilion (涼亭): red columns, a green glazed roof and an old man resting inside. */
function pavilion(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  art(c, `pv:${x}:${y}`, x - 42, y - 68, 84, 72, (cc) => {
    shadow(cc, x, y, 30, 5, 0.3, 34);
    block(cc, x - 30, y - 6, 60, 6, '#b8b0a0', '#d0c8b8', '#8a8278');
    for (let i = x - 28; i < x + 28; i += 6) rect(cc, 'rgba(0,0,0,0.08)', i, y - 5, 0.6, 4);
    for (const px of [x - 24, x + 20]) block(cc, px, y - 42, 4, 36, '#b83a2e', '#d8564a', '#7a2218');
    block(cc, x - 18, y - 18, 36, 4, '#8a6a40', '#a8865a', '#5a4020');
    block(cc, x - 18, y - 22, 36, 3, '#8a6a40', '#a8865a', '#5a4020');
  });
  drawCharacter(c, ELDER, x - 6, y - 8, { ...idle(t, 4), facing: 'down' });
  block(c, x - 18, y - 12, 36, 4, '#a8865a', '#c8a070', '#6a5030');
  for (const px of [x - 16, x + 13]) rect(c, '#5a4020', px, y - 8, 2, 3);
  art(c, `pv2:${x}:${y}`, x - 44, y - 70, 88, 72, (cc) => {
    for (const px of [x - 29, x + 25]) {
      block(cc, px, y - 38, 4, 34, '#b83a2e', '#d8564a', '#7a2218');
      rect(cc, '#ffd34e', px + 1, y - 28, 2, 8);
    }
    poly(cc, '#2f7a4a', [x - 38, y - 42, x + 38, y - 42, x + 21, y - 58, x - 21, y - 58]);
    poly(cc, '#3a9a5a', [x - 21, y - 58, x + 21, y - 58, x + 7, y - 65, x - 7, y - 65]);
    for (let i = x - 37; i < x + 37; i += 3) rect(cc, 'rgba(255,255,255,0.14)', i, y - 57, 1, 15);
    for (let k = y - 56; k < y - 42; k += 2.5) rect(cc, 'rgba(0,0,0,0.12)', x - 37, k, 74, 0.6);
    rect(cc, '#e8702a', x - 38, y - 43.4, 76, 1.8);
    poly(cc, '#e8702a', [x - 38, y - 42, x - 43, y - 49, x - 39.5, y - 49, x - 36, y - 44]);
    poly(cc, '#e8702a', [x + 38, y - 42, x + 43, y - 49, x + 39.5, y - 49, x + 36, y - 44]);
    rect(cc, '#e8702a', x - 21, y - 59, 42, 1.6);
    rect(cc, '#b83a2e', x - 1.2, y - 68, 2.4, 3.5);
    ellipse(cc, '#ffd34e', x, y - 68, 2.6, 2.6);
    rect(cc, '#7a2218', x - 38, y - 42, 76, 1.2);
    block(cc, x - 9, y - 50, 18, 6, '#2a1810', '#4a3020', '#1a0a05');
    txt(cc, '清風亭', x, y - 46.8, 4, '#ffd34e', 900);
  });
  // A red lantern hanging from the eave.
  const sw = Math.sin(t * 1.4) * 1.2;
  line(c, '#2a1810', 0.5, [x, y - 42, x + sw, y - 38]);
  ellipse(c, OUTLINE, x + sw, y - 34, 3.6, 4.4);
  ellipse(c, '#e2493b', x + sw, y - 34, 3, 3.8);
  rect(c, '#ffd34e', x + sw - 0.5, y - 30, 1, 2.4);
  if (isNight()) ellipse(c, 'rgba(255,100,60,0.3)', x + sw, y - 34, 8, 9);
}

/** Wooden pergola smothered in bougainvillea, with a bench underneath. */
function pergola(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 48;
  art(c, `pg:${x}:${y}:${w}`, x - w / 2 - 12, y - 46, w + 24, 50, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 3.5, 0.25, 20);
    for (const px of [x - w / 2, x + w / 2 - 3]) block(cc, px, y - 32, 3, 32, '#8a6a40', '#a8865a', '#5a4020');
    block(cc, x - w / 2 + 6, y - 15, w - 12, 5, '#a0703e', '#c09060', '#7a5028');
    block(cc, x - w / 2 + 6, y - 9, w - 12, 4, '#a0703e', '#c8986a', '#7a5028');
    for (const px of [x - w / 2 + 8, x + w / 2 - 10]) rect(cc, '#3a3e44', px, y - 5, 2, 5);
    block(cc, x - w / 2 - 6, y - 35, w + 12, 3, '#8a6a40', '#a8865a', '#5a4020');
    for (let i = x - w / 2 - 4; i < x + w / 2 + 4; i += 6) rect(cc, '#6a4a28', i, y - 38, 2.4, 5);
    for (let i = 0; i < Math.floor(w / 4); i++) {
      const vx = x - w / 2 - 6 + hash(i, 3, 9) * (w + 12), vy = y - 38 + hash(i, 4, 9) * 7;
      ellipse(cc, i % 3 ? '#3a8a3a' : '#4fa04a', vx, vy, 3.6, 2.6);
    }
    for (let i = 0; i < Math.floor(w / 3); i++) {
      const vx = x - w / 2 - 8 + hash(i, 5, 9) * (w + 16), vy = y - 42 + hash(i, 6, 9) * 11;
      ellipse(cc, i % 4 ? '#e0407a' : '#ff78a8', vx, vy, 1.8, 1.4);
    }
    for (let i = 0; i < 5; i++) {
      const vx = x - w / 2 + 2 + (i * (w - 4)) / 4;
      line(cc, '#3a7a3a', 0.8, [vx, y - 33, vx + 1, y - 25 + (i % 2) * 3]);
      ellipse(cc, '#e0407a', vx + 1, y - 25 + (i % 2) * 3, 1.6, 1.3);
    }
  });
}

/** Outdoor fitness gear beloved of grandparents: twist discs, an air walker, shoulder wheels. */
function exercise(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  shadow(c, x, y, 8, 2);
  const blue = '#2f6fb0';
  if (v === 0) {
    rect(c, blue, x - 1.3, y - 18, 2.6, 18);
    rect(c, blue, x - 9, y - 20, 18, 1.6);
    for (const [dx, dy] of [[-7, -4], [7, -4], [0, -10]]) {
      rect(c, blue, x + dx - 0.6, y + dy, 1.2, 4 - dy - 4);
      ellipse(c, OUTLINE, x + dx, y + dy, 4.8, 2.3);
      ellipse(c, '#ffd34e', x + dx, y + dy, 4.1, 1.7);
      rect(c, 'rgba(0,0,0,0.2)', x + dx - 2.6, y + dy - 0.3, 5.2, 0.6);
    }
  } else if (v === 1) {
    rect(c, blue, x - 1.3, y - 25, 2.6, 25);
    rect(c, blue, x - 11, y - 25, 22, 1.8);
    const sw = Math.sin(t * 2) * 3;
    for (const s of [-1, 1]) {
      line(c, '#9aa6ae', 1.3, [x + s * 6, y - 23.5, x + s * 6 + sw * s, y - 4]);
      block(c, x + s * 6 + sw * s - 3.5, y - 4, 7, 2.4, '#ffd34e');
    }
  } else {
    rect(c, blue, x - 1.3, y - 23, 2.6, 23);
    rect(c, blue, x - 14, y - 16, 28, 1.6);
    for (const s of [-1, 1]) {
      c.strokeStyle = '#ffd34e';
      c.lineWidth = 1.5;
      c.beginPath();
      c.arc(x + s * 8, y - 15, 5.2, 0, Math.PI * 2);
      c.stroke();
      const a = t * (s > 0 ? 1.5 : -1.2);
      line(c, '#e2493b', 1.1, [x + s * 8, y - 15, x + s * 8 + Math.cos(a) * 5.2, y - 15 + Math.sin(a) * 5.2]);
    }
  }
}

/** Lily pads and koi circling in the pond (`w`×`h` area above the anchor; the middle strip is left for the bridge). */
function koi(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const w = d.w ?? 160, h = d.h ?? 64;
  const x0 = x - w / 2, y0 = y - h;
  for (let i = 0; i < 10; i++) {
    const px = x0 + 8 + hash(i, 1, 21) * (w - 16), py = y0 + 8 + hash(i, 2, 21) * (h - 16);
    if (Math.abs(px - x) < 22) continue;
    ellipse(c, '#3f8a4a', px, py, 4.2, 2.7);
    poly(c, '#5aa860', [px, py, px + 4.2, py - 1.1, px + 4.2, py + 1.1]);
    if (i % 3 === 0) {
      ellipse(c, '#ff9fc0', px - 1, py - 2, 1.7, 1.3);
      ellipse(c, '#ffe0ea', px - 1, py - 2.7, 0.9, 0.7);
    }
  }
  const cols = ['#ff8a3a', '#ffffff', '#ffd34e', '#ff5a3a', '#ffffff', '#ff8a3a'];
  for (let i = 0; i < 6; i++) {
    const side = i % 2 ? 1 : -1;
    const cx = x + side * (w / 4 + 4);
    const a = t * (0.3 + i * 0.06) * side + i * 1.7;
    const rx = w / 4 - 16, ry = h / 2 - 11;
    const kx = cx + Math.cos(a) * rx, ky = y - h / 2 + Math.sin(a) * ry;
    const ang = Math.atan2(Math.cos(a) * ry * side, -Math.sin(a) * rx * side);
    c.save();
    c.translate(kx, ky);
    c.rotate(ang);
    ellipse(c, 'rgba(0,0,0,0.15)', 0.5, 1.6, 4, 1.8);
    ellipse(c, cols[i], 0, 0, 4, 1.8);
    if (i % 2 === 0) ellipse(c, '#e2493b', -1, 0, 1.3, 0.9);
    else ellipse(c, '#ff5a3a', 1, -0.4, 1.1, 0.7);
    const tail = Math.sin(t * 6 + i) * 1.2;
    poly(c, cols[i], [-3.5, 0, -6.2, -1.9 + tail, -6.2, 1.9 + tail]);
    rect(c, '#1b1d24', 2.4, -0.7, 0.6, 0.6);
    c.restore();
    const k = (t * 0.5 + i / 6) % 1;
    c.strokeStyle = `rgba(255,255,255,${0.35 * (1 - k)})`;
    c.lineWidth = 0.6;
    c.beginPath();
    c.ellipse(kx, ky, 2 + k * 6, 1 + k * 3, 0, 0, Math.PI * 2);
    c.stroke();
  }
}

/** Red wooden rails along both sides of the one-tile pond bridge (`h` = length upwards). */
function bridgeRail(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const h = d.h ?? 80;
  for (const s of [-1, 1]) {
    const rx = x + s * 7;
    rect(c, 'rgba(0,0,0,0.25)', rx - 1.6, y - h - 2, 3.2, h + 2);
    rect(c, '#c8463a', rx - 1, y - h - 4, 2, h + 4);
    for (let k = 0; k <= h; k += 10) {
      block(c, rx - 1.6, y - k - 7, 3.2, 7, '#b83a2e', '#d8564a', '#7a2218');
      rect(c, '#ffd34e', rx - 1.2, y - k - 7.6, 2.4, 0.8);
    }
  }
}

/** Small grandstand by the track with a few spectators under a tin roof. */
function bleachers(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 64;
  art(c, `bl:${x}:${y}:${w}`, x - w / 2 - 8, y - 48, w + 16, 52, (cc) => {
    shadow(cc, x, y, w / 2 + 2, 3, 0.3, 16);
    for (let r = 2; r >= 0; r--) {
      const ty = y - 6 - r * 7;
      block(cc, x - w / 2, ty - 7, w, 7, ['#5a8fc0', '#4f80b0', '#4a76a0'][r], '#8ab8e0', '#2a4a6a');
      rect(cc, 'rgba(0,0,0,0.2)', x - w / 2 + 1, ty - 0.8, w - 2, 0.8);
      const n = Math.floor(w / 12);
      for (let i = 0; i < n; i++) {
        if (hash(i, r, 33) < 0.42) continue;
        const sx = x - w / 2 + 6 + i * 12 + hash(i, r, 34) * 4;
        const top = ['#e2493b', '#ffd34e', '#f4f1ea', '#3a8fd9', '#57c278', '#ff9f43'][Math.floor(hash(i, r, 35) * 6)];
        const skin = ['#f6cfa8', '#e8b48c', '#c88a5a'][Math.floor(hash(i, r, 36) * 3)];
        block(cc, sx - 3.2, ty - 14, 6.4, 7.6, top);
        ellipse(cc, OUTLINE, sx, ty - 16.6, 3.6, 3.6);
        ellipse(cc, skin, sx, ty - 16.6, 3, 3);
        rect(cc, hash(i, r, 37) < 0.3 ? '#9a948a' : '#2a1d16', sx - 3, ty - 19.6, 6, 2.6);
        if (hash(i, r, 38) < 0.3) block(cc, sx - 2, ty - 24, 4, 4, '#e2493b');
      }
    }
    for (const px of [x - w / 2 + 2, x + w / 2 - 4]) rect(cc, '#4a4e52', px, y - 44, 2, 20);
    block(cc, x - w / 2 - 5, y - 48, w + 10, 5, '#8aa0a8', '#a8c0c8', '#5a7078');
    for (let i = x - w / 2 - 3; i < x + w / 2 + 5; i += 3) rect(cc, 'rgba(255,255,255,0.18)', i, y - 47, 1, 3);
    block(cc, x - 14, y - 51, 28, 5, '#e2493b', '#f06a5a', '#a8261c');
    txt(cc, '加油!', x, y - 48.6, 3.6, '#ffffff', 900);
  });
}

function cones(c: Ctx, d: Decor): void {
  const { x, y } = d;
  for (const [dx, dy] of [[-7, 0], [1, -3], [7, 1]]) {
    shadow(c, x + dx, y + dy, 3.4, 1.2);
    block(c, x + dx - 4, y + dy - 1.6, 8, 1.6, '#e85a20');
    poly(c, OUTLINE, [x + dx - 3.4, y + dy - 1.6, x + dx + 3.4, y + dy - 1.6, x + dx + 1, y + dy - 11, x + dx - 1, y + dy - 11]);
    poly(c, '#ff7a2f', [x + dx - 2.6, y + dy - 1.6, x + dx + 2.6, y + dy - 1.6, x + dx + 0.7, y + dy - 10, x + dx - 0.7, y + dy - 10]);
    rect(c, '#ffffff', x + dx - 1.8, y + dy - 6.6, 3.6, 1.4);
  }
}

/** Stainless rooftop water tank (variant 1 adds a smaller second tank). */
function waterTank(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  rect(c, 'rgba(0,0,0,0.18)', x - 7, y - 1, 15, 2);
  for (const dx of [-5.5, 4.2]) rect(c, '#4a4e52', x + dx, y - 8, 1.4, 8);
  rect(c, '#4a4e52', x - 7, y - 8.6, 14, 1.4);
  block(c, x - 6, y - 23, 12, 15, '#c8ccd0', '#eef0f2', '#8a9096');
  ellipse(c, '#dfe3e6', x, y - 23, 6, 2.3);
  ellipse(c, 'rgba(255,255,255,0.75)', x - 1.2, y - 23.4, 2.2, 0.8);
  rect(c, 'rgba(0,0,0,0.12)', x - 5, y - 17, 10, 0.8);
  rect(c, 'rgba(0,0,0,0.12)', x - 5, y - 12, 10, 0.8);
  rect(c, 'rgba(255,255,255,0.35)', x - 4.6, y - 21, 1.2, 12);
  rect(c, '#3a8fd9', x + 6, y - 15, 1.3, 9);
  rect(c, '#3a8fd9', x + 6, y - 6.6, 5, 1.3);
  if (v) {
    block(c, x + 9, y - 17, 8, 10, '#c8ccd0', '#eef0f2', '#8a9096');
    ellipse(c, '#dfe3e6', x + 13, y - 17, 4, 1.6);
    rect(c, 'rgba(255,255,255,0.35)', x + 10.2, y - 15.5, 1, 7);
  }
}

/** Rooftop add-on (頂樓加蓋): tin shed; variant bits 1 blue roof, 2 TV antenna, 4 satellite dish, 8 laundry line. */
function roofShed(c: Ctx, d: Decor, _t: number): void {
  const { x, y } = d;
  const w = d.w ?? 40;
  const v = d.variant ?? 0;
  art(c, `rs:${x}:${y}:${w}:${v}`, x - w / 2 - 12, y - 40, w + 24, 44, (cc) => {
    rect(cc, 'rgba(0,0,0,0.2)', x - w / 2 + 2, y - 1, w + 2, 3);
    block(cc, x - w / 2, y - 15, w, 15, '#9aa0a6', '#b8bec4', '#6a7076');
    for (let i = x - w / 2 + 1; i < x + w / 2 - 1; i += 3) rect(cc, 'rgba(0,0,0,0.1)', i, y - 14, 0.8, 13);
    rect(cc, '#5a7a9a', x - 4, y - 11, 8, 6);
    rect(cc, 'rgba(255,255,255,0.5)', x - 3, y - 10, 2, 4);
    rect(cc, '#3a3e44', x - 0.4, y - 11, 0.8, 6);
    block(cc, x + w / 2 - 9, y - 12, 6, 12, '#6a6e72', '#8a9096', '#4a4e52');
    const col = v & 1 ? '#4a6a9a' : '#a85a3a';
    block(cc, x - w / 2 - 3, y - 22, w + 6, 8, col, shade(col, 0.25), shade(col, -0.3));
    for (let i = x - w / 2 - 2; i < x + w / 2 + 3; i += 3) rect(cc, 'rgba(255,255,255,0.15)', i, y - 21, 1, 6);
    for (let i = 0; i < 6; i++) ellipse(cc, 'rgba(120,60,30,0.45)', x - w / 2 + hash(i, 1, 44) * w, y - 19 + hash(i, 2, 44) * 5, 2.2, 1.1);
    if (v & 2) {
      const ax = x + w / 2 - 6;
      rect(cc, '#3a3e44', ax, y - 40, 1, 18);
      for (let k = 0; k < 4; k++) rect(cc, '#3a3e44', ax - 5 + k * 1.2, y - 39 + k * 3.2, 11 - k * 2.4, 0.8);
    }
    if (v & 4) {
      const dx = x - w / 2 + 7;
      rect(cc, '#3a3e44', dx - 0.5, y - 26, 1, 5);
      ellipse(cc, '#e8ecef', dx, y - 29, 4.4, 3.4);
      ellipse(cc, 'rgba(0,0,0,0.15)', dx + 0.4, y - 29, 3.2, 2.4);
      rect(cc, '#3a3e44', dx + 1, y - 29.5, 2.6, 0.8);
    }
    // Potted plants along the parapet: Taiwanese roofs are gardens too.
    for (let i = 0; i < 3; i++) {
      const px = x - w / 2 + 6 + i * 9;
      block(cc, px - 2.6, y - 4, 5.2, 4, '#b0603a', '#c97a50', '#8a4426');
      ellipse(cc, i % 2 ? '#3f9a4a' : '#5aaa52', px, y - 6, 3.4, 2.8);
    }
  });
  if (v & 8) {
    const x0 = x - w / 2 - 4, x1 = x + w / 2 + 4;
    line(c, '#3a3e44', 0.5, [x0, y - 27, x1, y - 27]);
    const n = Math.floor(w / 9);
    const wv = wind.at(x, y), wt = wind.time();
    for (let i = 0; i < n; i++) {
      const lx = x0 + 6 + i * 9;
      const sw = Math.sin(wt * 3 + i) * 1.6 * wv;
      const col = ['#3a8fd9', '#ffffff', '#ff9fc0', '#ffd34e', '#57c278'][i % 5];
      poly(c, col, [lx - 3, y - 27, lx + 3, y - 27, lx + 3 + sw, y - 20 + (i % 2) * 2, lx - 3 + sw, y - 20 + (i % 2) * 2]);
      rect(c, '#e2493b', lx - 3.4, y - 27.6, 0.8, 1.6);
      rect(c, '#e2493b', lx + 2.6, y - 27.6, 0.8, 1.6);
    }
  }
}

/** Pillar positions of an arcade: every 64 px from the west end plus the east end. */
export function arcadePillars(cx: number, w: number): number[] {
  const out: number[] = [];
  for (let px = cx - w / 2; px < cx + w / 2 - 6; px += 64) out.push(px);
  out.push(cx + w / 2 - 6);
  return out;
}

/** 騎樓: square pillars and a beam in front of a house, with plates and a doorbell. */
function arcade(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 96;
  const v = d.variant ?? 0;
  art(c, `ar:${x}:${y}:${w}:${v}`, x - w / 2 - 2, y - 36, w + 4, 40, (cc) => {
    const band = ['#6aa0c8', '#c8a060', '#8ab88a'][v % 3];
    block(cc, x - w / 2, y - 34, w, 5, '#d8d0c0', '#ece6d8', '#a8a090');
    rect(cc, band, x - w / 2 + 1, y - 33, w - 2, 2);
    rect(cc, 'rgba(0,0,0,0.3)', x - w / 2, y - 29, w, 1.6);
    arcadePillars(x, w).forEach((px, i) => {
      shadow(cc, px + 3, y, 4, 1.4, 0.25);
      block(cc, px, y - 30, 6, 30, '#c8c0b0', '#e0d8c8', '#8a8278');
      rect(cc, band, px + 0.8, y - 29, 4.4, 11);
      for (let k = y - 28; k < y - 19; k += 2.5) rect(cc, 'rgba(0,0,0,0.12)', px + 0.8, k, 4.4, 0.5);
      rect(cc, 'rgba(0,0,0,0.2)', px + 5, y - 29, 1, 29);
      if (i === 0) {
        block(cc, px + 0.8, y - 16, 4.4, 3.4, '#2f6fb0');
        rect(cc, '#ffffff', px + 1.6, y - 15, 2.8, 0.8);
        rect(cc, '#ffffff', px + 1.6, y - 13.8, 2.8, 0.8);
      }
      if (i === 1) {
        block(cc, px + 1.4, y - 12, 3.2, 3.2, '#e2493b');
        ellipse(cc, '#ffd34e', px + 3, y - 10.4, 0.7, 0.7);
      }
    });
  });
}

const SCOOT = ['#f4f1ea', '#e2493b', '#3a8fd9', '#1b1d24', '#57c278', '#ffd34e'];
/** A parked scooter: variant%6 colour, 8 flips it, 16 pink delivery box, 32 helmet on the seat. */
function scooter(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  const col = SCOOT[v % 6];
  const dir = v & 8 ? -1 : 1;
  shadow(c, x, y, 10, 2.4);
  ellipse(c, '#1b1d24', x - 7 * dir, y - 2, 2.7, 2.7);
  ellipse(c, '#1b1d24', x + 7 * dir, y - 2, 2.7, 2.7);
  ellipse(c, '#6a6e74', x - 7 * dir, y - 2, 1, 1);
  ellipse(c, '#6a6e74', x + 7 * dir, y - 2, 1, 1);
  block(c, x - 8, y - 9, 15, 6, col, shade(col, 0.3), shade(col, -0.3));
  block(c, x + dir * 4.5 - 2, y - 14, 4, 11, col, shade(col, 0.3), shade(col, -0.3));
  rect(c, '#2a1810', x + dir * 4 - 3.5, y - 15, 7, 1.3);
  block(c, x - dir * 3 - 4, y - 11.4, 8, 2.8, '#2a2a30');
  ellipse(c, '#fff6c0', x + dir * 7.5, y - 11, 1.3, 1.3);
  rect(c, '#c0392b', x - dir * 8 - 0.5, y - 8, 1, 1.4);
  if (v & 16) {
    block(c, x - dir * 7 - 4.5, y - 17, 9, 7.5, '#ff2d8a', '#ff6ab0', '#b0105a');
    txt(c, '快送', x - dir * 7, y - 13.4, 3.2, '#ffffff', 900);
  } else if (v & 32) {
    ellipse(c, OUTLINE, x - dir * 2.5, y - 12.2, 3.2, 2.6);
    ellipse(c, ['#ffffff', '#e2493b', '#3a8fd9'][v % 3], x - dir * 2.5, y - 12.2, 2.7, 2.2);
    rect(c, 'rgba(0,0,0,0.5)', x - dir * 2.5 - 2, y - 12, 4, 1);
  }
}

/** A kerbside row of parked scooters (side view), colours and extras varied per slot. */
function scooterRow(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 64;
  const v = d.variant ?? 0;
  const n = Math.max(1, Math.floor(w / 17));
  const step = w / n;
  for (let i = 0; i < n; i++) {
    const sx = x - w / 2 + step * (i + 0.5);
    const col = Math.floor(hash(i, v, 51) * 6);
    const extra = hash(i, v, 52) < 0.3 ? 32 : hash(i, v, 53) < 0.15 ? 16 : 0;
    scooter(c, { kind: 'tw_scooter', x: Math.round(sx), y: y + (i % 2), variant: col | (v % 2 ? 8 : 0) | extra });
  }
}

/** Vegetable plot in an empty lot: raised rows of greens, bamboo stakes with beans, a watering can. */
function vegPlot(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 80, h = d.h ?? 40;
  art(c, `vp:${x}:${y}:${w}:${h}`, x - w / 2 - 6, y - h - 26, w + 12, h + 30, (cc) => {
    rect(cc, '#7a5a3a', x - w / 2, y - h, w, h);
    for (let i = 0; i < w * h * 0.08; i++) rect(cc, hash(i, 1, 61) < 0.5 ? '#6a4a2a' : '#8a6a4a', x - w / 2 + hash(i, 2, 61) * w, y - h + hash(i, 3, 61) * h, 1.2, 0.8);
    const rows = Math.floor(h / 9);
    for (let r = 0; r < rows; r++) {
      const ry = y - h + 6 + r * 9;
      rect(cc, '#5a3e24', x - w / 2 + 2, ry + 2.5, w - 4, 2.4);
      const kind = r % 3;
      for (let i = 0; i < Math.floor(w / 6); i++) {
        const px = x - w / 2 + 4 + i * 6 + hash(i, r, 62) * 2;
        if (kind === 0) {
          ellipse(cc, '#3f9a4a', px, ry, 2.8, 2.2);
          ellipse(cc, '#6ac060', px - 0.6, ry - 0.8, 1.6, 1.2);
        } else if (kind === 1) {
          ellipse(cc, '#2f7a3a', px, ry, 2.4, 1.8);
          rect(cc, '#e0e8d8', px - 0.6, ry - 1.6, 1.2, 1.6);
        } else {
          ellipse(cc, '#4fa04a', px, ry + 0.5, 2, 1.6);
          if (i % 3 === 0) ellipse(cc, '#e2493b', px + 0.8, ry - 0.5, 1, 1);
        }
      }
    }
    // Bamboo trellis along the back with climbing beans.
    for (let i = 0; i < Math.floor(w / 12); i++) {
      const px = x - w / 2 + 6 + i * 12;
      rect(cc, '#c8b070', px - 0.5, y - h - 22, 1, 24);
      for (let k = 0; k < 4; k++) ellipse(cc, k % 2 ? '#3f9a4a' : '#5ab050', px + (k % 2 ? 2 : -2), y - h - 18 + k * 5, 2.2, 1.6);
    }
    rect(cc, '#c8b070', x - w / 2 + 4, y - h - 20, w - 8, 0.8);
    // Low fence of sticks and a watering can.
    for (let px = x - w / 2; px < x + w / 2; px += 5) rect(cc, '#8a6a40', px, y - 4, 1, 5);
    rect(cc, '#8a6a40', x - w / 2, y - 3, w, 0.8);
    block(cc, x + w / 2 - 8, y - 9, 6, 6, '#3a8fd9', '#5aa8e8', '#2a6fb0');
    rect(cc, '#2a6fb0', x + w / 2 - 3, y - 8, 4, 1);
  });
}

const BANNERS: [string, string, string][] = [['歡迎新生', '#e2493b', '#ffd34e'], ['運動會', '#2f6fb0', '#ffffff'], ['福德宮 平安', '#ffd34e', '#c0392b'], ['社區清潔日', '#2f9a5a', '#ffffff']];
/** Vertical cloth banner on a bamboo pole. */
function banner(c: Ctx, d: Decor, _t: number): void {
  const { x, y } = d;
  const [text, bg, fg] = BANNERS[(d.variant ?? 0) % BANNERS.length];
  shadow(c, x, y, 2, 1);
  rect(c, '#6a5a3a', x - 0.9, y - 58, 1.8, 58);
  const sw = Math.sin(wind.time() * 2.5 + x * 0.1) * wind.at(x, y) * 2.2;
  poly(c, bg, [x + 1, y - 56, x + 10.5, y - 56, x + 10.5 + sw, y - 18, x + 1 + sw * 0.8, y - 18]);
  rect(c, shade(bg, -0.35), x + 1, y - 56, 9.5, 0.9);
  for (let k = 0.25; k < 1; k += 0.3) rect(c, 'rgba(0,0,0,0.1)', x + 1 + sw * k * 0.8, y - 56 + k * 38, 9.5 - k, 1);
  c.font = `900 5px ${CJK}`;
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillStyle = fg;
  const chars = [...text.replace(' ', '')];
  chars.forEach((ch, i) => c.fillText(ch, x + 5.8 + sw * (i / chars.length) * 0.9, y - 51 + i * 6));
}

/** Street vendor cart under a striped umbrella: sugar-cane juice (0) or douhua (1). */
function cart(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  const [name, col] = v ? ['豆花', '#f2c230'] : ['甘蔗汁', '#2f9a5a'];
  shadow(c, x, y, 16, 3);
  drawCharacter(c, v ? VENDOR : JUICE, x + 3, y - 8, idle(t, 6));
  for (const dx of [-10, 8]) {
    ellipse(c, '#1b1d24', x + dx, y - 1, 3.2, 3.2);
    ellipse(c, '#9aa0a6', x + dx, y - 1, 1.2, 1.2);
  }
  block(c, x - 14, y - 14, 28, 12, '#e8e0d0', '#f8f4ec', '#b0a898');
  rect(c, '#c0b8a8', x - 13, y - 4, 26, 0.8);
  rect(c, '#8a8278', x - 13, y - 9, 26, 0.6);
  block(c, x - 13, y - 20, 26, 6.5, col, shade(col, 0.3), shade(col, -0.3));
  txt(c, name, x, y - 16.7, 4.4, '#ffffff', 900, 'rgba(0,0,0,0.3)');
  if (v) {
    for (let i = 0; i < 3; i++) block(c, x - 10 + i * 7.5, y - 24, 5.5, 4.5, '#ffffff', '#ffffff', '#d8d0c8');
    block(c, x + 7, y - 27, 6, 7, '#9aa0a6', '#c0c6cc', '#6a7076');
  } else {
    for (let i = 0; i < 5; i++) rect(c, i % 2 ? '#8ab84a' : '#a8c860', x - 11 + i * 4.5, y - 32, 2, 12);
    block(c, x + 4, y - 26, 8, 6, '#3a3e44', '#5a5e64', '#2a2e34');
    block(c, x - 12, y - 13, 4, 2.4, '#ffd34e');
  }
  // Umbrella.
  rect(c, '#3a3e44', x + 11, y - 46, 1.3, 32);
  ellipse(c, OUTLINE, x + 11, y - 45, 20, 7);
  ellipse(c, '#e2493b', x + 11, y - 45, 19, 6.2);
  for (let i = -2; i <= 2; i += 2) poly(c, '#ffffff', [x + 11, y - 45, x + 11 + i * 7 - 3, y - 45 + Math.sqrt(Math.max(0, 1 - ((i * 7 - 3) / 19) ** 2)) * 6.2, x + 11 + i * 7 + 3, y - 45 + Math.sqrt(Math.max(0, 1 - ((i * 7 + 3) / 19) ** 2)) * 6.2]);
  ellipse(c, '#ffd34e', x + 11, y - 51, 1.2, 1.2);
}

/** Someone sitting: a bench with one (or two, +16) people, or a stool (+32) with one elder. */
function sit(c: Ctx, d: Decor, t: number): void {
  const { x, y } = d;
  const v = d.variant ?? 0;
  const first = LOOKS[v % 16];
  if (v & 32) {
    shadow(c, x, y, 5, 1.6);
    drawCharacter(c, first, x, y - 1, { ...idle(t, v), facing: v & 64 ? 'left' : 'down' });
    ellipse(c, '#e2493b', x, y - 6.5, 4.4, 1.8);
    rect(c, '#b83a2e', x - 3.4, y - 6.5, 1.2, 6.5);
    rect(c, '#b83a2e', x + 2.2, y - 6.5, 1.2, 6.5);
    return;
  }
  const second = v & 16 ? LOOKS[(v + 7) % 16] : null;
  shadow(c, x, y, 13, 2.2);
  block(c, x - 12, y - 17, 24, 6, '#a0703e', '#c09060', '#7a5028');
  rect(c, 'rgba(0,0,0,0.2)', x - 11, y - 14, 22, 0.6);
  for (const lx of [x - 10, x + 8]) rect(c, '#3a3e44', lx, y - 7, 2, 7);
  drawCharacter(c, first, second ? x - 6 : x, y - 2, idle(t, v));
  if (second) drawCharacter(c, second, x + 6, y - 2, idle(t, v + 3));
  block(c, x - 12, y - 10, 24, 4.5, '#a0703e', '#c8986a', '#7a5028');
}

/** School gate: two stone pillars with name plates and a blue arch sign over the opening (`w`). */
function gate(c: Ctx, d: Decor): void {
  const { x, y } = d;
  const w = d.w ?? 96;
  art(c, `gt:${x}:${y}:${w}`, x - w / 2 - 14, y - 60, w + 28, 64, (cc) => {
    for (const s of [-1, 1]) {
      const px = x + s * (w / 2 + 4);
      shadow(cc, px, y, 7, 2.4, 0.3, 36);
      block(cc, px - 6, y - 42, 12, 42, '#b8b0a0', '#d0c8b8', '#8a8278');
      block(cc, px - 7, y - 45, 14, 4, '#8a8278', '#a8a098', '#6a6258');
      for (let k = y - 38; k < y - 4; k += 6) rect(cc, 'rgba(0,0,0,0.1)', px - 5, k, 10, 0.7);
      block(cc, px - 4, y - 36, 8, 24, '#2a1810', '#4a3020', '#1a0a05');
      rect(cc, s < 0 ? '#f4f1ea' : '#e2493b', px - 3, y - 35, 6, 22);
      cc.font = `900 4.4px ${CJK}`;
      cc.textAlign = 'center';
      cc.textBaseline = 'middle';
      cc.fillStyle = s < 0 ? '#2a1810' : '#ffd34e';
      [...(s < 0 ? '立德國中' : '勤學守禮')].forEach((ch, i) => cc.fillText(ch, px, y - 31.5 + i * 5.2));
      rect(cc, '#3a3e44', px - 1, y - 56, 2, 11);
    }
    block(cc, x - w / 2 - 10, y - 56, w + 20, 11, '#2f6fb0', '#4f8fd0', '#1f4f80');
    txt(cc, '立德國民中學', x, y - 50.4, 6.6, '#ffffff', 900, 'rgba(0,0,0,0.4)');
    rect(cc, '#ffd34e', x - w / 2 - 9, y - 46.5, w + 18, 1);
    rect(cc, '#ffd34e', x - w / 2 - 9, y - 55, w + 18, 0.8);
  });
}

// ---------------------------------------------------------------------------
// Ground paint (static, drawn with the structures — zero per-frame cost)
// ---------------------------------------------------------------------------

/** Painted markings on the ground. `x,y` is the top-left; see each variant. */
export function wallDecorTown(c: Ctx, d: Decor): boolean {
  const { x, y } = d;
  const w = d.w ?? 16, h = d.h ?? 16;
  switch (d.kind) {
    case 'tw_emblem': {
      // School emblem painted on the plaza paving (worn).
      const r = w / 2;
      c.save();
      c.globalAlpha = 0.55;
      c.strokeStyle = '#1f3a8a';
      c.lineWidth = 2.4;
      c.beginPath();
      c.arc(x, y, r, 0, Math.PI * 2);
      c.stroke();
      c.strokeStyle = '#c0392b';
      c.lineWidth = 1.2;
      c.beginPath();
      c.arc(x, y, r - 4, 0, Math.PI * 2);
      c.stroke();
      c.fillStyle = '#1f3a8a';
      c.beginPath();
      for (let i = 0; i < 10; i++) {
        const a = -Math.PI / 2 + (i * Math.PI) / 5;
        const rr = i % 2 ? r * 0.32 : r * 0.7;
        c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
      }
      c.closePath();
      c.fill();
      txt(c, '立德', x, y + r * 0.84 - 3, 5, '#1f3a8a', 900);
      c.restore();
      return true;
    }
    case 'tw_court': {
      // Half basketball court: baseline on top, key, free-throw circle, three-point arc.
      c.save();
      c.strokeStyle = 'rgba(255,255,255,0.8)';
      c.lineWidth = 1.2;
      c.fillStyle = 'rgba(200,80,60,0.28)';
      c.strokeRect(x, y, w, h);
      const kw = w * 0.34, kh = h * 0.5;
      c.fillRect(x + w / 2 - kw / 2, y, kw, kh);
      c.strokeRect(x + w / 2 - kw / 2, y, kw, kh);
      c.beginPath();
      c.arc(x + w / 2, y + kh, kw / 2, 0, Math.PI * 2);
      c.stroke();
      c.beginPath();
      c.arc(x + w / 2, y + 6, w * 0.44, 0.08, Math.PI - 0.08);
      c.stroke();
      c.beginPath();
      c.arc(x + w / 2, y + 6, 4, 0, Math.PI);
      c.stroke();
      c.restore();
      return true;
    }
    case 'tw_roadPaint': {
      const v = d.variant ?? 0;
      c.save();
      if (v === 0) {
        // Two-stage left turn box for scooters (機車待轉區).
        c.fillStyle = 'rgba(200,60,50,0.45)';
        c.fillRect(x, y, w, h);
        c.strokeStyle = 'rgba(255,255,255,0.85)';
        c.lineWidth = 1;
        c.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
        txt(c, '待轉區', x + w / 2, y + h / 2, Math.min(5, h * 0.42), 'rgba(255,255,255,0.9)', 900);
      } else if (v === 1) {
        txt(c, '慢', x + w / 2, y + h / 2, h * 0.95, 'rgba(255,255,255,0.8)', 900);
      } else if (v === 2) {
        // Straight-ahead arrow.
        c.fillStyle = 'rgba(255,255,255,0.8)';
        c.fillRect(x + w / 2 - 1.2, y + 5, 2.4, h - 5);
        c.beginPath();
        c.moveTo(x + w / 2, y);
        c.lineTo(x + w / 2 + 4, y + 6);
        c.lineTo(x + w / 2 - 4, y + 6);
        c.closePath();
        c.fill();
      } else if (v === 3) {
        c.fillStyle = 'rgba(255,255,255,0.85)';
        c.fillRect(x, y, w, h);
      } else {
        // Kerb line: red = no stopping, yellow = no parking.
        c.fillStyle = v === 4 ? 'rgba(220,50,40,0.85)' : 'rgba(240,200,40,0.85)';
        c.fillRect(x, y, w, h);
      }
      c.restore();
      return true;
    }
    case 'tw_drain': {
      // Storm-drain grating at the kerb.
      rect(c, '#3a3e44', x, y, w, h);
      for (let i = x + 1.5; i < x + w - 1; i += 2.5) rect(c, '#1b1d24', i, y + 0.8, 1, h - 1.6);
      rect(c, 'rgba(255,255,255,0.25)', x, y, w, 0.6);
      return true;
    }
    case 'tw_hopscotch': {
      c.save();
      c.strokeStyle = 'rgba(255,255,255,0.55)';
      c.lineWidth = 0.9;
      const s = 9;
      const rows: number[] = [1, 2, 1, 2, 1];
      let cy = y;
      rows.forEach((n, r) => {
        for (let i = 0; i < n; i++) {
          const cx = x + (n === 1 ? 0 : (i - 0.5) * s);
          c.strokeRect(cx - s / 2, cy - s, s, s);
          txt(c, String(r * 2 + i + 1), cx, cy - s / 2, 4, 'rgba(255,255,255,0.55)', 700);
        }
        cy -= s;
      });
      c.restore();
      return true;
    }
    default:
      return false;
  }
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

registerPropDrawers({
  decor: (c, d, t) => {
    switch (d.kind) {
      case 'tw_pole':
        pole(c, d, t);
        return true;
      case 'tw_flagpole':
        flagpole(c, d, t);
        return true;
      case 'tw_noticeBoard':
        noticeBoard(c, d);
        return true;
      case 'tw_bikeShed':
        bikeShed(c, d);
        return true;
      case 'tw_recycle':
        recycle(c, d);
        return true;
      case 'tw_storeSign':
        storeSign(c, d, t);
        return true;
      case 'tw_breakfast':
        breakfast(c, d, t);
        return true;
      case 'tw_postbox':
        postbox(c, d);
        return true;
      case 'tw_dog':
        dog(c, d, t);
        return true;
      case 'tw_cat':
        cat(c, d, t);
        return true;
      case 'tw_temple':
        temple(c, d, t);
        return true;
      case 'tw_pavilion':
        pavilion(c, d, t);
        return true;
      case 'tw_pergola':
        pergola(c, d);
        return true;
      case 'tw_exercise':
        exercise(c, d, t);
        return true;
      case 'tw_koi':
        koi(c, d, t);
        return true;
      case 'tw_bridgeRail':
        bridgeRail(c, d);
        return true;
      case 'tw_bleachers':
        bleachers(c, d);
        return true;
      case 'tw_cones':
        cones(c, d);
        return true;
      case 'tw_waterTank':
        waterTank(c, d);
        return true;
      case 'tw_roofShed':
        roofShed(c, d, t);
        return true;
      case 'tw_arcade':
        arcade(c, d);
        return true;
      case 'tw_scooter':
        scooter(c, d);
        return true;
      case 'tw_scooterRow':
        scooterRow(c, d);
        return true;
      case 'tw_vegPlot':
        vegPlot(c, d);
        return true;
      case 'tw_banner':
        banner(c, d, t);
        return true;
      case 'tw_cart':
        cart(c, d, t);
        return true;
      case 'tw_sit':
        sit(c, d, t);
        return true;
      case 'tw_gate':
        gate(c, d);
        return true;
      case 'tw_solid':
        return true; // invisible collision box (arcade pillars)
      default:
        return false;
    }
  },
  animatedDecor: ['tw_pole', 'tw_flagpole', 'tw_breakfast', 'tw_dog', 'tw_cat', 'tw_temple', 'tw_pavilion', 'tw_exercise', 'tw_koi', 'tw_roofShed', 'tw_banner', 'tw_cart', 'tw_sit', 'tw_storeSign'],
});
